/**
 * New Car Loan (NCL) Insert API Service
 * Handles new car loan application submission
 *
 * Refactored from: 3in1cms/bajajfinserv-web/ui.solid/src/lib/connectionManager/nclInsertApi.ts
 * Logic preserved AS-IS, using Redux instead of SolidJS signals
 */

import { COMMON_SSO_ACCESS_TOKEN } from "../constants/sso";
import { logPageActionToNewRelic } from "@repo/shared-utils/newRelic-log-utils";
import { getLoginState } from "@repo/shared-utils/insert-params";
import { request } from "../core/request";
import { API_RESPONSE_CODES, BFL_WHEELES_ERROR, OPERATION_IDS } from "./constants";
import { INCLApiResponse, INCLInsertRequest } from "../types/nclTypes";

/**
 * Insert new car loan application
 *
 * @param data - New car loan application request data
 * @returns Promise with new car loan insert response
 *
 * @example
 * ```typescript
 * const response = await nclInsert({
 *   mobileNo: "9876543210",
 *   journeyName: "NCL_FRESH",
 *   carMake: "Toyota",
 *   carModel: "Camry",
 *   loanAmount: "500000"
 * });
 * ```
 */
export const nclInsert = async (data: INCLInsertRequest): Promise<INCLApiResponse> => {
  try {
    const response = (await request({
      operationId: OPERATION_IDS.nclInsert,
      requestData: data,
    })) as INCLApiResponse;

    if (response.statusCode !== API_RESPONSE_CODES.STATUS_CODE_7001) {
      throw new Error(JSON.stringify(response));
    }

    return response;
  } catch (error) {
    logPageActionToNewRelic(BFL_WHEELES_ERROR, {
      component: "New Car Loan Insert",
      pageUrl: typeof window !== "undefined" ? window.location.href : "",
      message: (error as Error).message,
      debugInfo: JSON.stringify({
        loginState: getLoginState(),
        hasAToken: typeof window !== "undefined" && !!localStorage.getItem(COMMON_SSO_ACCESS_TOKEN),
        hasMobile: !!data.mobileNo,
      }),
    });
    return {} as INCLApiResponse;
  }
};
